<script setup lang="ts">
import TextButton from '@/components/input/Btn/TextButton.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import TopSlideDownModal from '@/corePagesModule/components/TopSlideDownModal.vue';
import { i18n } from '@/i18n';
import { ICombinedOrder, ICombinedOrderWithBusinessAndBasis } from '@/InventoryControlModule/domains/entities/InventoryOrder';
import { useInventoryStore } from '@/InventoryControlModule/pages/useInventoryStore';
import SvgLoader from '@/utils/SvgLoader.vue';
import { storeToRefs } from 'pinia';
import { computed, ref, StyleValue, watch } from 'vue';
import { MAX_DISPLAY_COMBINED_ORDER_ITEMS, MAX_DISPLAY_COMBINED_ORDER_ERROR_MESSAGE } from '@/InventoryControlModule/constants/index';
import { ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { useForms } from '@/corePagesModule/composables/useForm';
import { OrderBasisFactory } from '@/coreDomainsModule/factories/OrderBasisFactory';
import { OrderBasisService } from '@/coreDomainsModule/services/orderBasisService';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { CombinedOrderService } from '@/InventoryControlModule/domains/services/combinedOrderService';

const t = i18n.t;

const inventoryStore = useInventoryStore();
const { state } = storeToRefs(inventoryStore);
const { toggleCheckedRowData, createDefaultOrderGroupTitle } = inventoryStore;

const isOpen = ref<boolean>(false);
const fieldItems = [
  {
    name: 'maxTotalAmount',
    rules: [ValidateRule.Required, ValidateRule.NegativeNumber],
  },
];

const { fieldState, allValidate, resetForm } = useForms(fieldItems);
watch(
  () => state.value.checkedAggregateBusinessIds,
  (val) => {
    if (val.length >= 2) {
      isOpen.value = true;
      if (state.value.combinedOrdersState.combinedOrderContents.maxTotalAmount) {
        fieldState['maxTotalAmount'].value.value = state.value.combinedOrdersState.combinedOrderContents.maxTotalAmount.toString();
      }
      allValidate();
    } else {
      if (isOpen.value) closeModal();
    }
  }
);

const numberMaxTotalAmount = computed(() => {
  return parseFloat(fieldState['maxTotalAmount'].value.value) ?? 0;
});

const selectedBusinessItems = computed(() => {
  return state.value.businessList.filter((business) => state.value.checkedAggregateBusinessIds.includes(business.businessId));
});

const calcedOrders = computed((): ICombinedOrder[] => {
  const combinedBusinessOrders = state.value.checkedAggregateBusinessIds.map((businessId) => {
    return { businessId: businessId, orders: Object.values(state.value.orderEditData[businessId]) };
  });
  return combinedBusinessOrders;
});

const calcedOrderTotalAmount = computed(() => {
  const targetMonthDiff = DateService.calculateDiffMonths(new Date(), DateService.convertIDateToDate(targetDate.value));
  return CombinedOrderService.quantize(
    calcedOrders.value.reduce((acc, order) => {
      const amount = order.orders[targetMonthDiff]?.amount;
      return acc + (!amount || isNaN(amount) ? 0 : amount);
    }, 0)
  );
});

const calcedOrderLoadFactor = computed(() => {
  if (isNaN(calcedOrderTotalAmount.value) || isNaN(numberMaxTotalAmount.value)) return 0;
  const val = (calcedOrderTotalAmount.value / numberMaxTotalAmount.value) * 100;
  return isFinite(val) ? val : 0;
});

const targetDate = computed(() => {
  const flatCalcedOrder = calcedOrders.value.map((order) => order.orders).flat();
  const earlyestDate = flatCalcedOrder.reduce(
    (earlyest, current) => {
      if (isNaN(current.amount) || current.amount === 0 || !current.crossDate) return earlyest;
      if (!earlyest) return current.crossDate;
      const currentDate = DateService.convertIDateToDate(current.crossDate);
      const earlyestDate = DateService.convertIDateToDate(earlyest);
      return currentDate < earlyestDate ? current.crossDate : earlyest;
    },
    { year: 9999, month: 12, day: 31 }
  );
  if (!earlyestDate) return DateService.convertDateToIDate(new Date());
  return earlyestDate;
});

const combinedOrders = computed((): ICombinedOrder[] => {
  return calcedOrders.value.map((combinedOrder) => {
    const filteredOrder = combinedOrder.orders.filter((order) => {
      return !isNaN(order.amount) && order.crossDate && DateService.compareDates(order.crossDate, targetDate.value) === 0;
    });
    return { businessId: combinedOrder.businessId, orders: filteredOrder };
  });
});

const errorMessage = computed(() => {
  const errMsgArray: string[] = [];
  if (!fieldState['maxTotalAmount'] || fieldState['maxTotalAmount'].isError.value) errMsgArray.push(t('上限数量が入力されていません'));
  if (numberMaxTotalAmount.value < calcedOrderTotalAmount.value) errMsgArray.push(t('合計数量が上限数を超えています'));
  if (!combinedOrders.value.every((combinedOrder) => combinedOrder.orders.length !== 0)) {
    errMsgArray.push(t('まとめ発注では全商品の納期を同じ日にする必要があります'));
  } else if (!combinedOrders.value.every((combinedOrder) => combinedOrder.orders.length === 1)) {
    errMsgArray.push(t('1つの取引の中に同じ希望納期の発注が2つ以上存在します'));
  }
  return errMsgArray.slice(0, MAX_DISPLAY_COMBINED_ORDER_ERROR_MESSAGE);
});
//['まとめ発注では全商品の納期を同じ日にする必要があります', '合計数量が上限数を超えています'];

const selectedBusinessStyle = computed((): StyleValue => {
  return {
    gridTemplateColumns: `auto repeat(${Math.min(
      state.value.checkedAggregateBusinessIds.length - 1,
      MAX_DISPLAY_COMBINED_ORDER_ITEMS
    )}, auto) auto`,
  };
});

/**
 * モーダルを閉じる
 */
const closeModal = () => {
  isOpen.value = false;
  resetForm(fieldItems);
};

/**
 * まとめ発注作成ボタンを押した際の処理
 */
const onNext = async () => {
  const confirmedOrdersWithBusinessAndBasis: Array<ICombinedOrderWithBusinessAndBasis | undefined> = combinedOrders.value.map(
    (combinedOrder) => {
      const orderBusiness = selectedBusinessItems.value.find((business) => business.businessId === combinedOrder.businessId);
      if (!orderBusiness || combinedOrder.orders.length !== 1) return undefined;
      const createdOrderBasisInfo = OrderBasisFactory.create({
        snapshotLatestInventoryAmount: orderBusiness.inventory,
        snapshotExecutionDate: orderBusiness.executionDate?.getTime(),
        snapshotPackage: Number(orderBusiness.package),
        snapshotMoq: orderBusiness.MOQ,
        userAdjustedLeadTime:
          orderBusiness.leadTime && orderBusiness.leadTimeUnit
            ? { value: orderBusiness.leadTime, unit: orderBusiness.leadTimeUnit }
            : undefined,
        userAdjustedStdInventoryIndex: orderBusiness.stdInventoryIndices?.['標準在庫指数'],
        nextMonthlyInfos: OrderBasisService.getNextMonthlyInfosForCreateOrder(
          orderBusiness.customerAnnounce ?? [],
          (orderBusiness.customerAnnounce ?? []).map((cf) => ({
            ...cf,
            amount: (cf.amount * (state.value.forecastAccuracyData[orderBusiness.businessId]?.settingAccuracy || 100)) / 100,
          })),
          orderBusiness.recommend,
          orderBusiness,
          combinedOrder.orders.slice(0, 1).map((order) => ({ ...order, status: OrderStatus.ApprovedAwaitingDeliveryDate }))
        ),
        pastMonthlyInfos: OrderBasisService.getPastMonthlyInfosForCreateOrder(
          orderBusiness.customerAnnounce ?? [],
          orderBusiness.salesResults ?? []
        ),
      });
      return {
        business: orderBusiness,
        order: combinedOrder.orders[0],
        basis: createdOrderBasisInfo,
      };
    }
  );
  if (!confirmedOrdersWithBusinessAndBasis.every((order) => order !== undefined)) {
    // 取引情報がなぜかないとき
    showInfoModal({
      modalTitle: t('エラーが発生しました'),
      modalDescription: t('リロードして再度お試しください'),
      backButtonLabel: t('確認'),
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  }

  const tempGroupName = await createDefaultOrderGroupTitle(confirmedOrdersWithBusinessAndBasis.length);

  state.value.combinedOrdersState.combinedOrderContents = {
    groupName: tempGroupName,
    orderCount: confirmedOrdersWithBusinessAndBasis.length,
    totalAmount: calcedOrderTotalAmount.value,
    maxTotalAmount: numberMaxTotalAmount.value,
    desiredDeliveryDate: targetDate.value,
    loadFactor: calcedOrderLoadFactor.value,
    confirmedOrdersWithBusinessAndBasis: confirmedOrdersWithBusinessAndBasis,
  };
};
</script>
<template>
  <TopSlideDownModal :is-open="isOpen" :z-index="20" :style="{ padding: '16px' }">
    <div class="combine-order-modal">
      <div class="modal-main">
        <div class="summary">
          <SvgLoader name="shoppingCart" />
          <div class="summary__container summary__container--title">
            <div class="summary__text-bold">{{ t('まとめ発注選択中の件数', { number: state.checkedAggregateBusinessIds.length }) }}</div>
            <div class="">{{ t('まとめ発注モード') }}</div>
          </div>
          <div class="summary__container summary__container--value">
            <div class="">{{ t('発注合計数量') }}</div>
            <div class="summary__text-bold">{{ calcedOrderTotalAmount.addComma() }}</div>
          </div>
          <div class="summary__container summary__container--value">
            <div class="">{{ t('希望納期') }}</div>
            <div class="summary__text-bold">{{ `${DateService.convertIYearMonthDayToYYYYMMDDWithSlash(targetDate, true)}` }}</div>
          </div>
          <div class="summary__container summary__container--value">
            <div class="">{{ t('積載率') }}</div>
            <div class="summary__text-bold">{{ calcedOrderLoadFactor.roundToDecimal(2) }}%</div>
          </div>
          <div class="summary__container summary__container--value">
            <div class="">{{ t('上限数量') }}</div>
            <NumberInputWithComma
              :model-value="numberMaxTotalAmount"
              class="summary__input"
              @input="
                (val) => {
                  fieldState['maxTotalAmount'].setValue(val, true);
                }
              "
            />
          </div>
        </div>
        <div class="control">
          <div class="control__error">
            <div v-for="(msg, index) in errorMessage" :key="index" class="control__error-row">
              <SvgLoader name="alertCellError" />
              {{ t(msg) }}
            </div>
          </div>
          <TextButton :disabled="!!errorMessage.length" @click="onNext">
            {{ t('まとめ発注作成') }}
          </TextButton>
          <button @click="closeModal">
            <SvgLoader name="closeBoldBlue" />
          </button>
        </div>
      </div>
      <div class="modal-selected" :style="selectedBusinessStyle">
        {{ t('選択商品：') }}
        <div
          v-for="(businessItem, index) in selectedBusinessItems.slice(0, MAX_DISPLAY_COMBINED_ORDER_ITEMS)"
          :key="index"
          class="selected-item"
        >
          {{ businessItem.businessNumber }}
          /
          {{ businessItem.businessName }}
          <button @click="toggleCheckedRowData(businessItem, false)">
            <SvgLoader name="close" />
          </button>
        </div>
        <div v-if="state.checkedAggregateBusinessIds.length > MAX_DISPLAY_COMBINED_ORDER_ITEMS">
          {{ t('他n件', { number: state.checkedAggregateBusinessIds.length - MAX_DISPLAY_COMBINED_ORDER_ITEMS }) }}
        </div>
      </div>
    </div>
  </TopSlideDownModal>
</template>
<style lang="scss" scoped>
.combine-order-modal {
  width: 100%;
  height: 100%;
  padding: 16px;
  border-radius: 8px;
  background-color: $semanticPrimarySubdued;
  display: grid;
  grid-template-rows: auto 24px;
  gap: 8px;
  color: $semanticPrimary;
}

.modal-main {
  height: 100%;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid $semanticPrimary;
}

.modal-selected {
  width: max-content;
  height: 24px;
  display: grid;
  align-items: center;
  overflow: hidden;
  gap: 4px;
}

.summary {
  display: grid;
  grid-template-columns: repeat(6, auto);
  gap: 16px;
  &__container {
    height: 36px;
    display: grid;
    grid-template-rows: auto auto;
    gap: 4px;
    padding-right: 32px;

    font-size: 14px;
    line-height: 14px;
    &--title {
      border-right: 1px solid $semanticPrimary;
    }
    &--value {
      justify-items: center;
    }
  }
  &__text-bold {
    font-size: 18px;
    font-weight: 700;
    line-height: 18px;
  }
  &__input {
    width: 88px;
    border-radius: 4px;
    color: $textSecondary;
    border: 1px solid $borderDefault;
    text-align: center;
  }
}

.control {
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 8px;
  &__error {
    padding: 0 24px;
    &-row {
      display: grid;
      grid-template-columns: auto auto;
      justify-content: start;
      align-items: center;
      color: $semanticError;
      font-size: 12px;
    }
  }
}

.selected-item {
  width: fit-content;
  height: 24px;
  padding: 2px 4px;
  overflow: hidden;

  border: 1px solid $borderSecondary;
  border-radius: 4px;

  display: grid;
  grid-template-columns: auto 16px;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  background-color: white;

  color: $textSecondary;
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
}
</style>
